import {
  IconButton,
  type IconButtonProps,
} from "@-/dev.improve.haniverse.components.ui.button.icon";
import {
  ToolbarPopoverButton,
  ToolbarPopoverSwitchRow,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.popover";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useCallback } from "react";

export type PrimaryControlsStripExpansionMutePushButtonProps = Omit<
  IconButtonProps,
  "accessibilityLabel" | "active" | "name" | "onPress"
>;

/**
 * The session's sound override.
 *
 * With a `label` it is a toolbar tray pill that opens a popover holding the on/off switch (the
 * tray's contract: every pill opens a popover, and a boolean setting is a switch). Without one it
 * is the circular push button of the header expansion strip and the summary screen, which toggles
 * on press and paints its pushed state.
 */
export function PrimaryControlsStripExpansionMutePushButton({
  className,
  disabled,
  iconColor,
  iconSize,
  isLoading,
  label,
  testID,
  ...pushButtonProps
}: PrimaryControlsStripExpansionMutePushButtonProps) {
  const [isMutedOverride, setIsMutedOverride] = useContextSelectors(
    NavContext,
    (c) => c.isMutedOverride,
    (c) => c.setIsMutedOverride,
  );
  const iconName = isMutedOverride ? "MdVolumeOff" : "MdVolumeUp";

  const handlePress = useCallback(() => {
    setIsMutedOverride(!isMutedOverride);
  }, [isMutedOverride, setIsMutedOverride]);

  if (label === undefined) {
    return (
      <IconButton
        accessibilityLabel={isMutedOverride ? "Sound muted" : "Sound on"}
        active={isMutedOverride}
        className={className}
        disabled={disabled}
        iconColor={iconColor}
        iconSize={iconSize}
        isLoading={isLoading}
        name={iconName}
        onPress={handlePress}
        testID={testID}
        {...pushButtonProps}
      />
    );
  }

  return (
    <ToolbarPopoverButton
      accessibilityLabel="Sound options"
      className={className}
      disabled={disabled}
      iconColor={iconColor}
      iconSize={iconSize}
      isLoading={isLoading}
      label={label}
      name={iconName}
      testID={testID}
    >
      <ToolbarPopoverSwitchRow
        checked={!isMutedOverride}
        description="Play card audio during this session."
        label="Sound"
        onCheckedChange={(checked) => setIsMutedOverride(!checked)}
        testID="sound-enabled"
      />
    </ToolbarPopoverButton>
  );
}
